import { useQuery } from '@tanstack/react-query';
import type { AnalyticsOnboardingResponse, AnalyticsResponse, OnboardingCohortRange } from '@mygate/common';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';

function useAnalyticsRequest<T extends AnalyticsResponse | AnalyticsOnboardingResponse>(
  range: OnboardingCohortRange | null,
  enabled: boolean,
) {
  const staffId = useAuthStore(state => state.staff?.id);
  return useQuery({
    queryKey: range
      ? ['analytics', staffId, 'onboarding', range.from, range.to]
      : ['analytics', staffId, 'investments'],
    enabled: Boolean(staffId) && enabled,
    staleTime: 5 * 60_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async ({ signal }) => {
      const path = range
        ? `/api/staff/analytics/onboarding?${new URLSearchParams({ from: range.from, to: range.to })}`
        : '/api/staff/analytics';
      const response = await api.request<T>(path, { method: 'GET', signal });
      if (!response.data) throw new Error('Die Analytics-Antwort enthält keine Daten.');
      return response.data;
    },
  });
}

/** Holdings at the latest snapshots; a period does not apply to a point-in-time state. */
export function useInvestmentAnalytics(enabled = true) {
  return useAnalyticsRequest<AnalyticsResponse>(null, enabled);
}

export function useOnboardingAnalytics(range: OnboardingCohortRange, enabled = true) {
  return useAnalyticsRequest<AnalyticsOnboardingResponse>(range, enabled);
}
